<template>
    <a-spin :spinning="!showEditor">
        <editor
            tinymce-script-src="//cdn.sxnuoyun.com/tinymce/5.2.2/tinymce.min.js"
            :model-value="value"
            @update:model-value="onUpdateValue"
            :init="init"
            :disabled="disabled"
            v-bind="$attrs"
        />
    </a-spin>
</template>

<script>
import {ref, reactive, onMounted} from "vue";
import editor from "@tinymce/tinymce-vue";
import {file_service} from "@/api/adminService";
import {message} from "ant-design-vue";

export default {
    name: "TinymceEditor",
    inheritAttrs: false,
    components: {
        editor,
    },
    emits: ["update:value"],
    props: {
        value: {
            type: String,
            default: "",
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        menubar: {
            type: [Array, String],
            default() {
                return [
                    "file",
                    "edit",
                    "view",
                    "insert",
                    "format",
                    "tools",
                    "table",
                    "help",
                ];
            },
        },
        toolbar: {
            type: [Array, String],
            default() {
                return [
                    "undo " + //撤销
                    "redo " + //重做
                    "| " +
                    "bold " + //粗体
                    "italic " + //斜体
                    "underline " + //下划线
                    "strikethrough " + //删除线
                    "| " +
                    "fontselect " + //字体
                    "fontsizeselect " + //字号
                    "formatselect " + //基块
                    "| " +
                    "alignleft " + //左边对齐
                    "aligncenter " + //中间对齐
                    "alignright " + //右边对齐
                    "alignjustify " + //两端对齐
                    "| " +
                    "indent2em" + //首行缩进
                    "|" +
                    "outdent " + //减少缩进
                    "indent " + //增加缩进
                    "|  " +
                    "numlist " + //编号列表
                    "bullist " + //项目符号
                    "| " +
                    "forecolor " + //文本颜色
                    "backcolor " + //背景颜色
                    "removeformat " + //清除格式
                    "| " +
                    "pagebreak " + //分页符
                    "| " +
                    "charmap " + //特殊符号
                    "emoticons " + //表情符号
                    "| " +
                    "fullscreen " + //全屏幕
                    "preview " + //预览
                    "print " + //打印
                    "| " +
                    "image " + //插入/编辑图片
                    // "media " +//插入编辑媒体
                    "link " + //插入/编辑链接
                    "anchor " + //锚点
                    "codesample " + //插入/编辑代码示例
                    "| " +
                    "ltr " + //由左到右
                    "rtl " + //由右到左
                    "",
                ];
            },
        },
        plugins: {
            type: [Array, String],
            default() {
                return [
                    "advlist",
                    "anchor",
                    "autolink",
                    "autosave",
                    "code",
                    "codesample",
                    "directionality",
                    "emoticons",
                    "fullscreen",
                    "help",
                    "hr",
                    "image",
                    "imagetools",
                    "importcss",
                    "insertdatetime",
                    "indent2em",
                    "link",
                    "lists",
                    "media",
                    "nonbreaking",
                    "noneditable",
                    "pagebreak",
                    "paste",
                    "preview",
                    "print",
                    // "quickbars", //隐藏
                    "save",
                    "searchreplace",
                    "table",
                    "textpattern",
                    "toc",
                    "visualblocks",
                    "visualchars",
                    "wordcount",
                ];
            },
        },
        height: {
            type: Number,
            default: 500,
        },
    },
    setup(props, {emit}) {
        const showEditor = ref(false);
        const init = reactive({
            language: "zh_CN", //将语言文件解压缩到您的tinymce/langs文件夹中。
            height: props.height,
            plugins: props.plugins,
            menubar: props.menubar,
            toolbar: props.toolbar,
            // content_css: "./css/codepen.min.css",//实现一致的样式
            toolbar_sticky: false, //工具栏固定顶部
            autosave_ask_before_unload: true, //卸载前自动保存询问
            autosave_interval: "30s", //自动保存间隔
            autosave_prefix: "{path}{query}-{id}-", //自动保存前缀
            autosave_restore_when_empty: false, //空时自动保存还原
            autosave_retention: "2m", //自动保存
            image_advtab: true, //允许向图像添加自定义样式
            statusbar: false, //显示隐藏状态栏
            images_upload_handler: (blobInfo, success, failure) => {
                let xhr, formData;
                xhr = new XMLHttpRequest();
                xhr.withCredentials = false;
                xhr.open("POST", file_service.upload);
                xhr.onload = function () {
                    let json;
                    if (xhr.status !== 200) {
                        failure("HTTP Error: " + xhr.status, {remove: true});
                        return;
                    }
                    json = JSON.parse(xhr.responseText);
                    if (!json || json.code !== 200) {
                        failure(json.msg, {remove: true});
                    } else {
                        success("/resources" + json.data.fileAddr);
                    }
                };
                formData = new FormData();
                formData.append("file", blobInfo.blob(), blobInfo.filename());
                xhr.send(formData);
            },
            file_picker_callback: function (callback) {
                //模拟出一个input用于添加本地文件
                let input = document.createElement('input');
                input.setAttribute('type', 'file');
                input.click();
                input.onchange = function () {
                    let file = this.files[0];
                    let xhr, formData;
                    xhr = new XMLHttpRequest();
                    xhr.withCredentials = false;
                    xhr.open('POST', file_service.upload);
                    xhr.onload = function () {
                        let json;
                        if (xhr.status !== 200) {
                            message.warn("HTTP Error: " + xhr.status);
                            return;
                        }
                        json = JSON.parse(xhr.responseText);
                        if (!json || json.code !== 200) {
                            message.warn(json.msg);
                        } else {
                            callback(`/resources${json.data.fileAddr}`, {text: `${json.data.fileOriginalName}.${json.data.fileType}`});
                        }
                    };
                    formData = new FormData();
                    formData.append('file', file, file.name);
                    xhr.send(formData);
                };
            },
            relative_urls: false,
            remove_script_host: false,
            quickbars_selection_toolbar: "bold italic | quicklink h2 h3 blockquote quickimage quicktable", //快速选择工具栏
            toolbar_mode: "sliding", //"floating", "sliding", "scrolling", "wrap"
            font_formats:
                "宋体=宋体,SimSun;" +
                "楷体=楷体,楷体_GB2312,SimKai;" +
                "黑体=黑体,SimHei;" +
                "隶书=隶书,SimLi;" +
                "andale mono=andale mono;" +
                "arial=arial,helvetica,sans-serif;" +
                "arial black=arial black,avant garde;" +
                "comic sans ms=comic sans ms;" +
                "impact=impact,chicago;" +
                "times new roman=times new roman;",
        });
        onMounted(() => {
            showEditor.value = true;
        });
        const onUpdateValue = (value) => {
            emit("update:value", value);
        };
        return {
            showEditor,
            init,
            onUpdateValue,
        };
    },
};
</script>

<style scoped>
</style>
